import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/context/AuthContext";
import { BarChart2, RefreshCw, TrendingUp, Award, FileText, Lock } from "lucide-react";

const MESES_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

function mesLabel(mes) {
  const [y, m] = mes.split("-");
  return `${MESES_PT[parseInt(m, 10) - 1]} ${y}`;
}

function fmtEur(v) {
  return (v || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

function rankBg(i) {
  if (i === 0) return "bg-yellow-400 text-yellow-900";
  if (i === 1) return "bg-slate-300 text-slate-700";
  if (i === 2) return "bg-amber-600 text-amber-100";
  return "bg-slate-100 text-slate-500";
}

function buildMonthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({ val, label: mesLabel(val) });
  }
  return opts;
}

// ── PIN Gate ──────────────────────────────────────────────────────────────────
function PinGate({ onUnlock }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const refs = [useRef(), useRef(), useRef(), useRef()];

  const handleDigit = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...digits];
    next[i] = val;
    setDigits(next);
    setError(false);
    if (val && i < 3) refs[i + 1].current.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      refs[i - 1].current.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const code = digits.join("");
    if (code === "1936") {
      onUnlock();
    } else {
      setError(true);
      setShake(true);
      setDigits(["", "", "", ""]);
      refs[0].current.focus();
      setTimeout(() => setShake(false), 600);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className={`bg-white rounded-2xl border border-slate-200 shadow-xl p-10 w-full max-w-sm text-center ${shake ? "animate-shake" : ""}`}
        style={shake ? { animation: "shake 0.5s ease" } : {}}>
        <style>{`@keyframes shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-8px)}40%,80%{transform:translateX(8px)}}`}</style>
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-[#0b679e]/10 mx-auto mb-5">
          <Lock size={26} className="text-[#0b679e]" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">Área Restrita</h2>
        <p className="text-sm text-slate-500 mb-7">Introduza o código de acesso para continuar</p>
        <form onSubmit={handleSubmit}>
          <div className="flex justify-center gap-3 mb-6">
            {digits.map((d, i) => (
              <input
                key={`pin-digit-${i}`}
                ref={refs[i]}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={d}
                data-testid={`pin-digit-${i + 1}`}
                onChange={e => handleDigit(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                autoFocus={i === 0}
                className={`w-14 h-14 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none focus:ring-2 transition-colors ${
                  error ? "border-red-400 bg-red-50 focus:ring-red-300" : "border-slate-300 focus:border-[#0b679e] focus:ring-[#0b679e]/20"
                }`}
              />
            ))}
          </div>
          {error && (
            <p className="text-sm text-red-500 mb-4 font-medium" data-testid="pin-error">
              Código incorreto. Tente novamente.
            </p>
          )}
          <button
            type="submit"
            data-testid="pin-confirm-btn"
            disabled={digits.some(d => d === "")}
            className="w-full py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-40 transition-all hover:opacity-90"
            style={{ backgroundColor: "#0b679e" }}
          >
            Confirmar
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Rendimento() {
  const [pinUnlocked, setPinUnlocked] = useState(false);
  const [mes, setMes] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rendimento?mes=${m}`, { headers: getAuthHeader() });
      setData(res.data);
      setForbidden(false);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // API, axios e getAuthHeader são imports estáveis de módulo — não precisam de dep array

  useEffect(() => { load(mes); }, [load, mes]);

  const colaboradores = data?.colaboradores || [];
  const totalGeral = colaboradores.reduce((s, c) => s + c.total_debito, 0);
  const totalLancamentos = data?.total_docs || 0;
  const destaque = colaboradores[0] || null;
  const monthOpts = buildMonthOptions();

  if (!pinUnlocked) {
    return <PinGate onUnlock={() => setPinUnlocked(true)} />;
  }

  if (forbidden) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-slate-400">
        <BarChart2 size={48} strokeWidth={1.2} className="mb-4 text-slate-300" />
        <p className="text-lg font-semibold text-slate-600">Acesso Restrito</p>
        <p className="text-sm mt-1">Esta área é exclusiva para Administração.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart2 size={22} className="text-[#0b679e]" />
            Rendimento por Colaborador
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Honorários lançados por utilizador · dados a partir de hoje
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="select-mes-rendimento"
            value={mes}
            onChange={e => setMes(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#0b679e]/30 bg-white"
          >
            {monthOpts.map(o => (
              <option key={o.val} value={o.val}>{o.label}</option>
            ))}
          </select>
          <button
            data-testid="btn-refresh-rendimento"
            onClick={() => load(mes)}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-xl text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Total Registado</p>
          <p className="text-2xl font-bold text-[#0b679e]" data-testid="total-registado">
            {loading ? "—" : fmtEur(totalGeral)}
          </p>
          <p className="text-xs text-slate-400 mt-1">{mesLabel(mes)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Lançamentos</p>
          <p className="text-2xl font-bold text-slate-800" data-testid="total-lancamentos">
            {loading ? "—" : totalLancamentos}
          </p>
          <p className="text-xs text-slate-400 mt-1">Registos com autor identificado</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Mais Ativo</p>
          <p className="text-lg font-bold text-slate-800 truncate" data-testid="mais-ativo">
            {loading || !destaque ? "—" : destaque.nome}
          </p>
          <p className="text-sm text-[#0b679e] font-semibold">
            {loading || !destaque ? "" : `${destaque.n_lancamentos} lançamento${destaque.n_lancamentos !== 1 ? "s" : ""}`}
          </p>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {(() => {
          if (loading) return (
            <div className="flex items-center justify-center h-48">
              <div className="w-8 h-8 border-4 border-[#0b679e] border-t-[#F5A623] rounded-full animate-spin" />
            </div>
          );
          if (colaboradores.length === 0) return (
            <div className="text-center py-16 text-slate-400">
              <TrendingUp size={40} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">Sem lançamentos com autor em {mesLabel(mes)}</p>
              <p className="text-sm mt-1 text-slate-400">Os novos honorários registados aparecerão aqui.</p>
            </div>
          );
          return (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-12">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Colaborador</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Lançamentos</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Total Débito</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Total Crédito</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Saldo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {colaboradores.map((c, i) => (
                  <tr key={c.nome} data-testid={`rendimento-row-${i + 1}`} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${rankBg(i)}`}>
                        {i === 0 ? <Award size={13} /> : i + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{c.nome}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#0b679e]/10 text-[#0b679e]">
                        {c.n_lancamentos}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-emerald-700">{fmtEur(c.total_debito)}</td>
                    <td className="px-4 py-3 text-right text-slate-500">{fmtEur(c.total_credito)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold text-sm ${c.saldo >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                        {fmtEur(c.saldo)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                <tr>
                  <td colSpan={3} className="px-4 py-3 text-sm font-bold text-slate-700">
                    Total ({colaboradores.length} colaborador{colaboradores.length !== 1 ? "es" : ""})
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-700 text-sm">{fmtEur(totalGeral)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
          );
        })()}
      </div>
    </div>
  );
}
